import React from 'react'

import { IconType } from '@factorialco/f0-react'
import { ProcurementTypeItemFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

interface UseProcurementTypeActionsProps {
  onEditProcurementType: (procurementType: ProcurementTypeItemFragment) => void
  onToggle: (procurementType: ProcurementTypeItemFragment) => void
  onDelete: (procurementType: ProcurementTypeItemFragment) => void
  onDuplicate: (procurementType: ProcurementTypeItemFragment) => void
  onRestore: (procurementType: ProcurementTypeItemFragment) => void
}

interface ProcurementTypeAction {
  label: string
  icon?: IconType
  variant?: 'default' | 'secondary' | 'danger'
  type: 'primary' | 'secondary' | 'other'
  onClick: () => void
}

export const useProcurementTypeActions = ({
  onEditProcurementType,
  onToggle,
  onDelete,
  onDuplicate,
  onRestore,
}: UseProcurementTypeActionsProps) => {
  const getActions = React.useCallback(
    (procurementType: ProcurementTypeItemFragment): ProcurementTypeAction[] => {
      const actions: ProcurementTypeAction[] = []

      // Add rest of actions when no identifier is present
      if (onEditProcurementType) {
        actions.push({
          label: i18n.t('procurement.purchase_requests.settings.types.actions_new.edit'),
          variant: 'secondary',
          type: 'other',
          onClick: () => onEditProcurementType(procurementType),
        })
      }

      // Toggle action - for software and custom types
      if (procurementType.enabled && onToggle) {
        actions.push({
          label: i18n.t('procurement.purchase_requests.settings.types.actions_new.disable'),
          type: 'other',
          onClick: () => onToggle(procurementType),
        })
      }

      if (onDuplicate) {
        actions.push({
          label: i18n.t('procurement.purchase_requests.settings.types.actions_new.duplicate'),
          type: 'other',
          onClick: () => onDuplicate(procurementType),
        })
      }

      if (!procurementType.enabled && !procurementType.deletedAt && onDelete) {
        actions.push({
          label: i18n.t('procurement.purchase_requests.settings.types.actions_new.archive'),
          variant: 'danger',
          type: 'other',
          onClick: () => onDelete(procurementType),
        })
      }

      if (procurementType.deletedAt && onRestore) {
        actions.push({
          label: i18n.t('procurement.purchase_requests.settings.types.actions_new.restore'),
          type: 'other',
          onClick: () => onRestore(procurementType),
        })
      }

      return actions
    },
    [onEditProcurementType, onToggle, onDelete, onDuplicate, onRestore]
  )

  return { getActions }
}
